import {CalendarDataResponse, Holiday} from "@t/event/CalendarData.ts";
import {DAY, formatDate, HOUR, MINUTE, parseDate, ParsedDate, sameDay, shiftDate} from "@t/util/DateUtil.ts";
import {Attendee, DiaryObject} from "@t/event/DiaryObject.ts";
import {Rect} from "@/event/calendar/Shapes.ts";
import {debounce} from "@/utils/Debounce.ts";
import {reactive, ref, watch, computed} from "vue";
import {dialogControls as dc, type FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {type FormData, FormPart} from "@t/fd/FormPart.ts";
import {ModalDialogDisplay} from "@t/ui/Dialog.ts";
import {loadFormDefinition, loadSourceType, SourceType, SrcType} from "@t/meta/SourceType.ts";
import {Event} from "@t/event/Event.ts";
import {Contact} from "@t/contact/Contact.ts";
import {addContextParams, AppContextMap, contextBuilder, ContextKey, getIntValue, hasValue, toQueryString} from "@t/fd/AppContext.ts";
import {defineStore, storeToRefs} from 'pinia';
import {env} from "@fd/Environment.ts";
import {useTodayStore} from "@/store/TodayStore.ts";
import {useForceInfoStore} from "@/store/ForceInfoStore.ts";
import {previewKey} from "@fd/helpers/Preview.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {isRoute} from "@/router/Router.ts";
import {useRouter} from "vue-router";
import {ApiErrorResponse} from "@t/util/ApiError.ts";
import type {ErrorMap} from "@fd/PartInjectKeys.ts";
import {hasType, isObjectRef, ObjectRef, objectRef, refToUID, uidToRef} from "@/utils/DragDropUtils.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {CommandHandler, CommandHandlerResult, FormEnvironment} from "@fd/Types.ts";
import {DEBUGGER} from "@/intouch";

export type UIEventState = "normal"	// the event is in normal display state
	| "move"						// the event is being moved to a different location
	| "resize-start"				// the event is being resized by moving start (duration change)
	| "resize-end"					// the event is being resized by moving end (duration change)
	| "create";						// the event is being created by pointer

/** Event with this ID isn't real event but create dummy object */
export const DUMMY_ID = refToUID(objectRef(0, 0));


/** This is internal format of event as used by the store */
export interface CalEvent {
	/** Unique ID of the event !!! may differ from primary key of event in CRM */
	uid: string;
	/** Name of the event */
	title: string;

	start: Date;
	end: Date;

	allDay?: boolean;
	place?: string;
	attendees?: Attendee[];	// list of attendees
	partyName?: string;		// customer name

	completed?: boolean;
	canceled?: boolean;
	comment?: string;
	priority?: number;

	/** ID of EventColor */
	color?: number;

	/** Can edit position and duration */
	movable?: boolean;

	/** Custom icon for this event */
	icon?: string;
	/** Optional URL to go on event click (if the target ends with '=' the client will append srcId) */
	target?:string;

	/** UI states (default is "normal") */
	state?: UIEventState
}


export interface EventRect extends Rect {
	/** Pointer to the event */
	event: CalEvent;

	/** Pointer detection is only valid if also inside this clipRect (if present) */
	clipRect?: Rect;

	/** Shape containing the whole event (can be used to detect pointer hover) */
	shape: Path2D;
	/** Shape to drag from if resizing from start */
	startDragShape?: Path2D;
	/** Shape to drag from if resizing from end */
	endDragShape?: Path2D;
}

export function timeRange(e:CalEvent):string {
	return timeFormat(e.start) + "-" + timeFormat(e.end);
}

export function timeFormat(d: Date) {
	let b = d.getHours().toString() + ":";
	let m = d.getMinutes().toString();
	if (m.length === 1) m = "0"+m;
	b += m;
	return b;
}

export type DirtyFunction = () => void;

export interface DateRange {
	wholeDay?: boolean;
	start: Date;
	end: Date;
}

function rangeToStr(r: DateRange) {
	if (r == null) return "NULL";
	return "["+r.start.toLocaleString()+" - "+r.end.toLocaleString()+"]";
}

/** Test if the tested range overlaps the current range */
export function overlaps(current:DateRange, tested:DateRange):boolean {
	if (current == null) return false;
	let tstart = tested.start.getTime();
	let tend = tested.end.getTime();
	let cstart = current.start.getTime();
	let cend = current.end.getTime();
	if (tend < tstart) {
		let swap = tend;
		tend = tstart;
		tstart = swap;
	}
	if (cend < cstart) {
		let swap = cend;
		cend = cstart;
		cstart = swap;
	}
	return tend > cstart && tstart < cend;
}


/** Test if the tested range is contained inside the container range */
export function contains(container: DateRange, tested: DateRange) {
	if (!container || !tested) return false;
	let tstart = tested.start.getTime();
	let tend = tested.end.getTime();
	let cstart = container.start.getTime();
	let cend = container.end.getTime();
	if (tend < tstart) {
		let swap = tend;
		tend = tstart;
		tstart = swap;
	}
	if (cend < cstart) {
		let swap = cend;
		cend = cstart;
		cstart = swap;
	}
	return tstart >= cstart && tend <= cend;
}


function minOf(d1:Date, d2:Date):Date {
	if (d1.getTime() <= d2.getTime()) return d1;
	return d2;
}

function maxOf(d1:Date, d2:Date):Date {
	if (d1.getTime() >= d2.getTime()) return d1;
	return d2;
}

function merge(...ranges:DateRange[]):DateRange {
	let result = null;
	for (let r of ranges) {
		if (result == null) result = r;
		else {
			if (r != null) result = { start: minOf(result.start, r.start), end: maxOf(result.end, r.end) };
		}
	}
	return result;
}

// align start and end with day borders
export function normalizeRange(start:Date, end:Date):DateRange {
	const s = new Date(start);
	const e = new Date(end);
	s.setHours(0, 0, 0, 0);	// first millisecond of the day
	shiftDate(e);
	e.setHours(0, 0, 0, 0);
	return { start: s, end: e };
}

function convertDiaryObject(e: DiaryObject):CalEvent {
	const parsedStart = parseDate(e.from);
	const parsedEnd = parseDate(e.to, true);
	return {
		uid: refToUID(objectRef(e.srcType, e.srcId, e.srcSubType)),
		title: e.subject,
		start: parsedStart.date,
		end: parsedEnd.date,
		allDay: parsedStart.wholeDay,
		icon: e.icon,
		place: e.place,
		completed: !!e.completed,
		canceled: !!e.canceled,
		comment: e.comment,
		partyName: e.partyName,
		priority: e.priority,
		color: e.color,
		movable: e.movable && e.durationEditable,
		attendees: e.attendees,
		target: e.target,
		state: "normal"
	};
}

export function convertDiaryObjects(source: DiaryObject[], manipulated?: CalEvent):CalEvent[] {
	const evts:CalEvent[] = [];
	for (let e of source) {
		const converted = convertDiaryObject(e);
		if (manipulated && manipulated.uid === converted.uid) manipulated = null;
		evts.push(converted);
	}
	if (manipulated) evts.push(manipulated);	// add if not already in loaded batch
	return evts;
}

export function uidToPreviewKey(uid:string):string {
	let parsed:ObjectRef = uidToRef(uid);
	return previewKey(parsed.type, parsed.id, parsed.subType);
}

export async function fetchCalendarData(env:FormEnvironment, type: number | string, id: number | string, start: Date, end: Date): Promise<CalendarDataResponse> {
	let endpoint = "api/calendars/";
	endpoint += type + "/" + id;
	endpoint = env.net.urlParam(endpoint, "start", start.toISOString());
	endpoint = env.net.urlParam(endpoint, "end", end.toISOString());
	const response = await env.net.fetch(endpoint);
	if (!response.ok) throw new Error("Cannot fetch calendar data");
	return response.json() as Promise<CalendarDataResponse>;
}

function convertCalEvent(event:CalEvent):Partial<DiaryObject> {
	const or: ObjectRef = uidToRef(event.uid);
	const dobj:Partial<DiaryObject> = { srcType: or.type };
	if (or.subType) dobj.srcSubType = or.subType;
	if (or.id) dobj.srcId = or.id && typeof or.id === "number" ? or.id : parseInt(or.id as string);

	dobj.subject = event.title;
	if (event.allDay) {
		dobj.from = formatDate(event.start, false, false);
		/** Move end 1s because the end points to 00:00 of the next day */
		dobj.to = formatDate(new Date(event.end.getTime() - 1000), false, false);
	} else {
		dobj.from = formatDate(event.start, true);
		dobj.to = formatDate(event.end, true);
	}
	dobj.place = event.place;
	dobj.completed = event.completed;
	dobj.canceled = event.canceled;
	dobj.comment = event.comment;
	dobj.priority = event.priority;
	dobj.color = event.color;
	dobj.target = event.target;
	return dobj;
}

type CollideFunction = (layer:CalEvent[], e:CalEvent) => boolean;

function collidePlaced(layer: CalEvent[], e: CalEvent, sameDayCollide: boolean = false) {
	const r:DateRange = { start: e.start, end: e.end };
	if (sameDayCollide && !e.allDay) {
		r.start = new Date(r.start);
		r.end = new Date(r.end);
		r.start.setHours(0, 0, 0, 0);
		r.end.setHours(0, 0, 0, 0);
		shiftDate(r.end);
	}
	for (let le of layer) {
		if (overlaps(le, r)) {
			return true;
		}
	}
	return false;
}

export function calculateEventLayers(events:CalEvent[], sameDayCollide = false):CalEvent[][] {
	const layers:CalEvent[][] = [];
	if (events.length === 0) return layers;
	const all = [...events];
	do {
		let layer:CalEvent[] = [];
		for (let i = 0; i < all.length; i++) {
			const e = all[i];
			if (!collidePlaced(layer, e, sameDayCollide)) {
				layer.push(e);
				all.splice(i--, 1);
			}
		}
		layers.push(layer);
	} while (all.length > 0);
	return layers;
}


/**
 * Calculate how many layers can the event span and still not collide with other events.
 * @param ev  event to check
 * @param layers  all precalculate layers
 * @param layerIndex index of the layer this event belongs to (only layers with greater index will be checked)
 */
export function calcLayerSpan(ev:CalEvent, layers:CalEvent[][], layerIndex:number):number {
	let span = 1;
	for (let i = layerIndex+1; i < layers.length; i++) {
		if (collidePlaced(layers[i], ev)) {
			break;
		} else {
			span++;
		}
	}
	return span;
}

/** How much time to wait before really contacting the server for a new data */
const NEED_WAIT_TIMEOUT = 100;

export function allDayAtNoon(pd:ParsedDate):ParsedDate {
	if (pd.wholeDay && pd.date) {
		const d = new Date(pd.date);
		d.setTime(d.getTime() + 12 * 3600000);
		return { wholeDay: true, date: d };
	}
	return pd;
}

function convertHolidayDate(h:Holiday):Holiday {
	const hc:Holiday = { ...h };
	if (typeof hc.day === 'string') {
		hc.day = parseDate(hc.day).date;
	}
	return hc;
}

const MAX_RANGE_AGE = 60000;	// 1 min.

// Action for original event handling (when moving to other position)
export const ORIGINAL_DELETE = 1;
export const ORIGINAL_CANCEL = 2;
export const ORIGINAL_FULFILL = 3;
export const ORIGINAL_MOVE = 4;
export const ORIGINAL_KEEP = 5;

interface Need {
	consumer: string;
	/** Milliseconds */
	start: number;
	/** Milliseconds */
	end: number;
}

const storeSetup = () => {

	const today = useTodayStore();
	const fis = useForceInfoStore();
	const nav = useNavigationStore();
	const router = useRouter();
	const dirtyData = ref<number>(0);

	/** All loaded events */
	const events = ref<CalEvent[]>([]);
	const holidays = ref<Holiday[]>([]);
	const manipulated = ref<CalEvent|null>(null);
	const originalEventAction = ref<number>(ORIGINAL_FULFILL);

	const { type, id } = storeToRefs(today);

	const needs = reactive<Need[]>([])

	const rangeStart = computed<number>(() => {
		let v = undefined;
		for (let n of needs) {
			if (v === undefined) v = n.start;
			else v = Math.min(v, n.start);
		}
		return v;
	});
	const rangeEnd = computed<number>(() => {
		let v = undefined;
		for (let n of needs) {
			if (v === undefined) v = n.end;
			else v = Math.max(v, n.end);
		}
		return v;
	});

	// const rangeStart = ref<Date>(null);
	// const rangeEnd = ref<Date>(null);

	const state = ref<"init" | "loading" | "loaded" | "dirty" | "error">("init");
	let lastLoad = 0;


	function fetchEvents() {
		if (rangeStart.value == null || rangeEnd.value == null) return;
		//console.log("Invoking load ");
		state.value = "loading";
		const start = new Date(rangeStart.value);
		const end = new Date(rangeEnd.value);
		fetchCalendarData(env, type.value, id.value, start, end)
			.then((cd) => {
				const newEvents: CalEvent[] = convertDiaryObjects(cd.events, manipulated.value);
				// const startParsed = parseDate(cd.start);
				// const endParsed = parseDate(cd.end, true);
				events.value.length = 0;
				events.value.push(...newEvents);
				holidays.value.length = 0;
				if (Array.isArray(cd.holidays)) {
					cd.holidays.forEach((h) => holidays.value.push(convertHolidayDate(h)));
				}
				state.value = "loaded";
				lastLoad = Date.now();
				dirtyData.value++;
			})
			.catch((err) => {
				console.error(err);
				state.value = "error";
			});
	}

	const debouncedFetchEvents = debounce(fetchEvents, NEED_WAIT_TIMEOUT);

	watch([rangeStart, rangeEnd, type, id], () => {
		if (state.value === "loaded") {
			state.value = "dirty"; // Mark as needing refresh
		}
		debouncedFetchEvents();
	});


	function manipulationStart(eventUID:string) {
		const e = findByUID(eventUID);
		manipulated.value = e;
	}

	function manipulationEnd():void {
		manipulated.value = null;
	}

	function findNeed(consumer:string):Need {
		for (let need of needs) {
			if (need.consumer === consumer) return need;
		}
		return null;
	}

	/**
	 * This way a consumer of calendar data can notify store that it needs access to
	 * the given date range.
	 * @param firstDay	start of interval (including)
	 * @param lastDay	end of the interval (including)
	 * @param consumer	name of consumer (recommended to supportu multiple consumers, e.g. split-calendar)
	 */
	function needRange(firstDay:Date, lastDay:Date, consumer:string = "main"): void {
		const r = normalizeRange(firstDay, lastDay);
		//console.log("Need range: "+consumer);
		const start = r.start.getTime();
		const end = r.end.getTime();
		const need = findNeed(consumer);
		if (!need) {
			needs.push({ consumer, start, end });
			return;
		}
		//console.log("needRange ["+consumer+"]: "+r.start+" ... "+r.end);
		const fromLastLoad = Date.now() - lastLoad;
		if (fromLastLoad > MAX_RANGE_AGE) {
			need.start = start;
			need.end = end;
		} else {
			if (start < need.start) need.start = start;
			if (end > need.end) need.end = end;
		}
	}

	function getEvents(start:Date, end:Date):CalEvent[] {
		return events.value.filter((e) => {
			return e.end.getTime() > start.getTime() && e.start.getTime() < end.getTime();
		});
	}

	/** Filter timed events */
	function getTimedEvents(start:Date, end:Date):CalEvent[] {
		return getEvents(start, end).filter((e) => !e.allDay);
	}

	/** Filter all-day events */
	function getAllDayEvents(start:Date, end:Date):CalEvent[] {
		return getEvents(start, end).filter((e) => e.allDay);
	}

	/** Return holiday for given day or null if there is none */
	function getHoliday(day:Date):Holiday|null {
		for (let hd of holidays.value) {
			if (sameDay(day, hd.day as Date)) return hd;
		}
		return null;
	}

	function findByUID(uid:string):CalEvent|undefined {
		for (let e of events.value) {
			if (uid === e.uid) return e;
		}
	}

	function update(eventUID:string|ObjectRef):UpdateBuilder {
		if (isObjectRef(eventUID)) eventUID = refToUID(eventUID);
		const event = findByUID(eventUID);
		if (!event) throw new Error("Cannot update unloaded event id=" + eventUID);
		const backup:CalEvent = { ...event };

		function uid():string {
			return event.uid;
		}

		/** Set event to be all-day */
		function makeAllDay():UpdateBuilder {
			if (!event.allDay) {
				const newStart = new Date(event.start);
				newStart.setHours(0, 0, 0, 0);
				const newEnd = new Date(newStart);
				shiftDate(newEnd);	// plus day
				event.start = newStart;
				event.end = newEnd;
				event.allDay = true;
			}
			return builder;
		}

		/** Set event to be time-based */
		function makeTimed():UpdateBuilder {
			if (event.allDay) {
				const newStart = new Date(event.start);
				newStart.setHours(12, 0, 0, 0);	// make noon
				const newEnd = new Date(newStart.getTime() + 3600000);	// make hour long
				event.start = newStart;
				event.end = newEnd;
				event.allDay = false;
			}
			return builder;
		}

		function setStart(startDateOrHour:Date|number, startMinute = 0):UpdateBuilder {
			if (typeof startDateOrHour === "number") {
				if (!event.allDay) {
					event.start.setHours(startDateOrHour, startMinute, 0, 0);
				}
			} else {
				if (event.allDay) {
					const newStart = new Date(startDateOrHour);
					newStart.setHours(0, 0, 0, 0);
					event.start = newStart;
				} else {
					event.start = startDateOrHour;
				}
			}
			return builder;
		}

		function setEnd(endDateOrHour:Date|number, endMinute = 0):UpdateBuilder {
			if (typeof endDateOrHour === "number") {
				if (!event.allDay) {
					event.end.setHours(endDateOrHour, endMinute, 0, 0);
				}
			} else {
				if (event.allDay) {
					const newEnd = new Date(endDateOrHour);
					if (newEnd.getHours() !== 0 || newEnd.getMinutes() !== 0 || newEnd.getSeconds() !== 0 || newEnd.getMilliseconds() !== 0) {
						// Must be aligned to the end of the day
						newEnd.setHours(0, 0, 0, 0);
						shiftDate(newEnd);
					}
					event.end = newEnd;
				} else {
					event.end = endDateOrHour;
				}
			}
			return builder;
		}

		function move(start:Date):UpdateBuilder {
			if (event.allDay) {
				const dayDuration = Math.max(1, Math.round(Math.abs(event.end.getTime() - event.start.getTime()) / (24 * 3600000)));
				const newStart = new Date(start);
				newStart.setHours(0, 0, 0, 0);
				const newEnd = new Date(newStart);
				shiftDate(newEnd, dayDuration);
				event.start = newStart;
				event.end = newEnd;
			} else {
				const msDuration = Math.abs(event.end.getTime() - event.start.getTime());
				const newStart = new Date(start);
				const newEnd = new Date(newStart.getTime() + msDuration);
				event.start = newStart;
				event.end = newEnd;
			}
			return builder;
		}

		/** Set event duration in ms or days*/
		function setDuration(msOrDayDuration:number):UpdateBuilder {
			if (event.allDay) {
				const newEnd = new Date(event.start);
				shiftDate(newEnd, msOrDayDuration);
				event.end = newEnd;
			} else {
				const newEnd = new Date(event.start.getTime() + msOrDayDuration);
				event.end = newEnd;
			}
			return builder;
		}

		/** Cancel changes - nothing will be sent to backend */
		function cancel():void {
			Object.assign(event, backup);
			event.state = "normal";
		}

		function changed():boolean {
			return !!event.allDay != !!backup.allDay
				|| event.start.getTime() !== backup.start.getTime()
				|| event.end.getTime() !== backup.end.getTime();
		}

		function normalizeRange():void {
			if (event.start.getTime() > event.end.getTime()) {
				const swap = event.start;
				event.start = event.end;
				event.end = swap;
			}
		}

		/** Send changes to the backend and return new version of the event */
		async function commit():Promise<CalEvent|null> {
			event.state = "normal";
			normalizeRange();
			if (!changed()) return event;
			let endpoint = "api/calendars/event";

			const body = convertCalEvent(event);
			const result = await env.net.fetch(endpoint, "PUT", body)
				.then(response => {
					if (response.ok) return response.json() as Promise<DiaryObject>;
					throw new Error("Cannot update the event");
				})
				.then((dobj) => {
					const updatedEvent: CalEvent = convertDiaryObject(dobj);
					Object.assign(event, updatedEvent);
					return event;
				})
				.catch((reason) => {
					// Update failed
					Object.assign(event, backup);	// return changes
					console.error(reason);
					return null;
				})
				.finally(() => dirtyData.value++);
		}

		const builder = { uid, makeAllDay, makeTimed, setStart, setEnd, move, setDuration, cancel, commit };
		return builder;
	}


	/**
	 * Moving existing event to a different time slot
	 */
	async function invokeEventMove(eventId:number, wholeDay:boolean, start:Date, end:Date):Promise<any> {
		const body:Partial<DiaryObject> = {
			srcType: SrcType.EVENT,
			srcId: eventId,
		};
		if (wholeDay) {
			body.from = formatDate(start, false, false);
			/** Move end 1s because the end points to 00:00 of the next day */
			body.to = formatDate(new Date(end.getTime() - 1000), false, false);
		} else {
			body.from = formatDate(start, true);
			body.to = formatDate(end, true);
		}

		let endpoint = "api/calendars/event?uncompleted=true";

		return await env.net.fetch(endpoint, "PUT", body)
			.then(response => {
				if (response.ok) {
					fetchEvents();
					cancelPlanner();
				}
			})
			.catch((reason) => {
				console.error(reason);
				return null;
			})
			.finally(() => dirtyData.value++);
	}

	function setEndpointId(endpoint: string, id: number | string):string {
		return endpoint.replace("{id}", id.toString());
	}

	/**
	 * Create a duplicate of an object specified by or:ObjectRef at given time slot
	 * @param wholeDay all day event will be created
	 * @param start	   time where the new object will be placed
	 * @param or       specification of the original
	 */
	async function invokeCopy(wholeDay:boolean, start:Date, or:ObjectRef):Promise<any> {
		let srcType = or.type;
		let srcSubType = or.subType;

		// Load srcType definition
		let editSrcType = await loadSourceType(env, srcType);

		// Load record data
		let loadEndpoint = setEndpointId("api"+editSrcType.edit, or.id);
		let rowObject:FormData = await env.net.fetch(loadEndpoint).then((resp) => {
			if (resp.ok) return resp.json();
			throw new Error("Unable to initialize object " + loadEndpoint);
		});

		// Calculate new time slot
		// Type specific initializations
		if (srcType === SrcType.EVENT) {
			const e = rowObject as Event;
			e.id = 0;
			//console.dir(e);
			//console.log("EVENT: ", e.start, e.end);
			const parsedStart = parseDate(e.start);
			const parsedEnd = parseDate(e.end);
			const range = alterRange(parsedStart.wholeDay, parsedEnd.date.getTime() - parsedStart.date.getTime(), wholeDay, start);

			e.start = formatDate(range.start, !wholeDay, false);
			e.end = formatDate(range.end, !wholeDay, false);
			console.log("UPDATED: ", e.start, e.end);

			e.completion = "AUTO";
			// No recurrent event
			delete e.repeat;
			delete e.repeatId;
			delete e.entered;
			delete e.enteredBy;
			delete e.modified;
			delete e.modifiedBy;
			e.completed = false;
			delete e.notification;
			delete e.symbol;
		} else if (srcType === SrcType.CONTACT) {
			const ct = rowObject as Contact;
			ct.id = 0;
			let dur = ct.callDuration;
			if (!dur) dur = wholeDay ? DAY : HOUR;
			ct.planContact = formatDate(start, !wholeDay, false);
			ct.callDuration = dur;
			ct.notifyFlag = 0;
			delete ct.contacted;
			delete ct.entered;
			delete ct.enteredBy;
			delete ct.modified;
			delete ct.modifiedBy;
			delete ct.notification;
			delete ct.symbol;
			delete ct.result;
			delete ct.audioURL;
			ct.stateCall = "UNKNOWN";
			delete ct.callUid;
		}

		// Store modified object
		let saveEndpoint = "api"+editSrcType.create;
		saveEndpoint = env.net.urlParam(saveEndpoint, "diaryObject", "true");
		await env.net.fetch(saveEndpoint, "POST", rowObject)
			.then((resp) => {
				if (resp.ok) {
					resp.json().then((dobj:DiaryObject) => {
						const newCalEvent = convertDiaryObject(dobj);
						events.value.push(newCalEvent);	// add newly created event to the local list
						dirtyData.value++;	// force screen update
					});
				} else {
					// Backend returned non-20xx status code (e.g. BAD_REQUEST)
					resp.json().then((er:ApiErrorResponse) => {
						console.error("Cannot duplicate calendar object: ", er);
						dirtyData.value++;	// force screen update
					});
				}
			})
			.catch((err) => {
				console.error("Cannot duplicate calendar object: ", err);
				dirtyData.value++;	// force screen update
			});
	}


	async function invokeCreate(wholeDay:boolean, start:Date, end:Date, or?:ObjectRef):Promise<any> {

		if (start.getTime() > end.getTime()) {
			// Flip if the dates are in wrong order
			const s = new Date(end);
			end = start;
			start = s;
		}

		let srcType = today.createSrcType;
		let srcSubType = today.createSrcSubType;
		if (hasType(or)) {
			srcType = or.type;
			srcSubType = or.subType;
		}
		const cttx = today.createContext;

		const followUpEvent:boolean = hasValue(cttx, ContextKey.EVENT);
		if (followUpEvent && originalEventAction.value === ORIGINAL_MOVE) return invokeEventMove(getIntValue(cttx, ContextKey.EVENT), wholeDay, start, end);

		// Load srcType definition
		let editSrcType = await loadSourceType(env, srcType);
		// Load form
		dialogForm.value = await loadFormDefinition(env, editSrcType.form);

		// Init start/end interval
		let startIso:string = null, endIso:string = null;
		if (start) startIso = formatDate(start, !wholeDay, false);
		if (end) endIso = wholeDay ? formatDate(new Date(end.getTime() - 1000), false, false) : formatDate(end, true);

		// Load initial data
		//const ctx = contextBuilder(dialogForm.value, dialogData.value, cttx);
		let initEndpoint = followUpEvent ? setEndpointId("api"+editSrcType.edit, getIntValue(cttx, ContextKey.EVENT)) : "api" + editSrcType.init;
		if (srcType === SrcType.EVENT) {
			if (startIso) initEndpoint = env.net.urlParam(initEndpoint, "start", startIso);
			if (endIso) initEndpoint = env.net.urlParam(initEndpoint, "end", endIso);
		}
		initEndpoint = addContextParams(initEndpoint, cttx);
		if (srcSubType) initEndpoint = env.net.urlParam(initEndpoint, "subType", srcSubType);
		initEndpoint = env.net.urlParam(initEndpoint, "start", startIso);
		initEndpoint = env.net.urlParam(initEndpoint, "end", endIso);
		if (today.type === "user" || today.type === "group") initEndpoint = env.net.urlParam(initEndpoint, "responsibles", today.id.toString());
		dialogData.value = await env.net.fetch(initEndpoint).then((resp) => {
			if (resp.ok) return resp.json();
			throw new Error("Unable to initialize object " + initEndpoint);
		});
		dialogControls.value = dc(env, ["cancel", "add"]);
		dialogReadOnly.value = false;	// allow edit

		if (followUpEvent) {
			// Remove primary key from the data
			delete (dialogData.value as Event).id;
		}

		// Obtain current app context

		if (srcType === SrcType.EVENT) {
			(dialogData.value as Event).start = startIso;
			(dialogData.value as Event).end = endIso;
		} else if (srcType === SrcType.CONTACT) {
			(dialogData.value as Contact).planContact = startIso;
			if (!wholeDay) {
				(dialogData.value as Contact).callDuration = Math.abs(end.getTime() - start.getTime());
			}
			(dialogData.value as Contact).showInCalendar = true;
		}

		function reportException(e) {
			dialogErrors.push({ level: "SEVERE", message: e.toString() });
		}

		dialogActionFunc = (cmd:string, ...extraArgs:any[]) => {
			if (cmd === "add") {
				return new Promise((resolve) => {
					let ep = "api"+editSrcType.create;
					dialogErrors.length = 0;	// clear errors
					if (followUpEvent) {
						ep = env.net.urlParam(ep, "prevSrcType", SrcType.EVENT);
						ep = env.net.urlParam(ep, "prevSrcId", getIntValue(cttx, ContextKey.EVENT));
						ep = env.net.urlParam(ep, "prevAction", originalEventAction.value);
					}
					env.net.fetch(ep, "POST", dialogData.value)
						.then((resp) => {
							//console.log("Reponse from the server OK = "+resp.ok+" status = "+resp.status);
							if (resp.ok) {
								/* status code is 2xx */
								isDialog.value = false;
								fetchEvents();
								cancelPlanner();
								fis.loadForceInfo();
								/* JSON contains the object */
								resp.json().then((obj) => {
									//console.log("Got object with id "+obj?.id);
									if (obj?.id) {
										nav.display(srcType, obj.id);	// Show the newly created item in the history
									}
									resolve("ok");
								}).catch(reason => {
									reportException(reason);
									resolve("fail");
								});
							} else if (resp.status === 400 /* validation error */) {
								/* JSON should contain ApiErrorResponse */
								resolve("fail");
								resp.json().then((aer:ApiErrorResponse) => {
									reportException(env.msg('error.validation'));
									if (aer.problems && aer.problems.length > 0) dialogErrors.push(...aer.problems);
								}).catch(reason => {
									reportException(reason);
								});
							} else {
								resolve("fail");
								reportException(env.msg('error.internal')+": "+resp.status);
							}
						})
						.catch((reason) => {
							console.error(reason);
							reportException(env.msg('error.internal')+": " + reason.toString());
							resolve("fail");
						});
				});
			} else if (cmd === "cancel") {
				isDialog.value = false;
				dirtyData.value++;
				return Promise.resolve("ok");	// OK
			}
			return Promise.resolve("pass");	// Unhandled
		};
		// Show dialog
		dialogErrors.length = 0;	// clear errors
		isDialog.value = true;
	}


	function invokeEdit(eventUID:string):any {
		const e = findByUID(eventUID);
		if (e) {
			const or = uidToRef(e.uid);
			if (e.target) {
				let url = e.target;
				if (url.endsWith('=')) url += or.id;
				if (isRoute(url)) router.push(url);
				else window.location.href = url;
			} else env.navigateTo(or.type, or.id, or.subType);
		}
	}


	const isDialog = ref<boolean>(false);
	const dialogForm = ref<InterfaceRoot<FormDefinition>|null>(null);
	const dialogControls = ref<FormPart|null>(null);
	const dialogData = ref<FormData|null>(null);
	const dialogDisplay = reactive<ModalDialogDisplay>({ mode: "modal", resizable: true, moveable: true });
	const dialogReadOnly = ref<boolean>(false);
	const dialogErrors:ErrorMap = reactive<ErrorMap>([]);

	let dialogClosedFunc:() => void;
	let dialogActionFunc:CommandHandler;

	function dialogClosed():void {
		isDialog.value = false;
		dirtyData.value++;	// repaint
		if (dialogClosedFunc) dialogClosedFunc();
	}

	function dialogAction(cmd:string, ...extraArgs):Promise<CommandHandlerResult> {
		if (dialogActionFunc) return dialogActionFunc(cmd, ...extraArgs);
		return Promise.resolve("pass");
	}

	function cancelPlanner() {
		env.net.fetch("api/calendars/cancel")
			.then((res) => {
				if (res.ok) {
					today.clearCreateContext();
				}
			}).catch((err) => { console.log(err) });
	}


	return {
		needRange,
		getEvents,
		getTimedEvents,
		getAllDayEvents,
		getHoliday,
		update,
		findByUID,
		manipulationStart, manipulationEnd,
		invokeCreate, invokeEdit, invokeCopy,
		dirtyData,

		cancelPlanner,
		originalEventAction,

		isDialog,
		dialogForm,
		dialogData,
		dialogReadOnly,
		dialogDisplay,
		dialogControls,
		dialogClosed,
		dialogAction,
		dialogErrors
	};
};


function normalizeDuration(wholeDay:boolean, dur:number):number {
	console.log("normalizeDuration ", dur);
	if (wholeDay) {
		if (dur < DAY) {
			console.log("returning day ");
			return DAY;
		}
		return Math.round(dur / DAY);
	} else {
		if (dur < (DAY/2)) {
			if (dur < (5 * MINUTE)) return HOUR;
			return dur;	// keep duration
		} else {
			return HOUR;
		}
	}
}

export function alterRange(currentWholeDay:boolean, currentDurationMs:number, targetWholeDay:boolean, targetStart:Date):DateRange {
	const r:Partial<DateRange> = { wholeDay: targetWholeDay };
	if (currentWholeDay === targetWholeDay) {
		// Same type - no need to normalize duration
		r.start = new Date(targetStart);
		r.end = new Date(targetStart.getTime() + currentDurationMs);
	} else {
		if (targetWholeDay) {
			// From timed to all day
			r.start = new Date(targetStart);
			r.end = new Date(targetStart);
		} else {
			// From all day to timed
			r.start = new Date(targetStart);
			r.end = new Date(r.start.getTime() + HOUR);
		}
	}
	return r as DateRange;
}

/**
 * Create a new calendar store with event cache.
 */
export const useCalendarStore = defineStore('calendar', storeSetup);
export const useSecondaryCalendarStore = defineStore('calendar-secondary', storeSetup);


export interface UpdateBuilder {
	/** Return UID of the event */
	uid: () => string;

	/** Set event to be all-day */
	makeAllDay: () => UpdateBuilder;
	/** Set event to be time-based */
	makeTimed: () => UpdateBuilder;
	setStart: (startDateOrHour:Date|number, startMinute?:number) => UpdateBuilder;
	setEnd: (endDateOrHour:Date|number, endMinute?:number) => UpdateBuilder;
	move: (start:Date) => UpdateBuilder;
	/** Set event duration in ms or days*/
	setDuration: (msOrDayDuration:number) => UpdateBuilder;

	/** Cancel changes - nothing will be sent to backend */
	cancel: () => void;
	/** Send changes to the backend and return new version of the event */
	commit: () => Promise<CalEvent|null>;
}

export type CalendarStore = ReturnType<typeof useCalendarStore>;
